import React, { useCallback, useEffect, useState } from 'react';
import { ValueWrapperArray, unwrapValue } from './ValueWrapper';
import Stack from '@mui/material/Stack';
import AddCircleOutlineIcon from '@mui/icons-material/AddCircleOutline';
import { GradientIconButton } from 'src/components/gradients/GradientIconButton';
import RemoveCircleOutlineIcon from '@mui/icons-material/RemoveCircleOutline';
import Typography from '@mui/material/Typography';
import { AnyInput } from './AnyInput';
import Box from '@mui/material/Box';

export interface ArrayInputProps<U> {
  input: ValueWrapperArray<U>;
  onChange: (value: Array<U>) => void;
  values: Array<U>;
}

export function ArrayInput<U>({
  input,
  onChange,
  values: valuesDefault,
}: ArrayInputProps<U>) {
  const { title, element: inputElement, initialElements } = input;
  const [values, setValues] = useState(valuesDefault || []);
  useEffect(() => {
    onChange(values);
  }, [values, onChange]);

  // eslint-disable-next-line @blumintinc/blumint/extract-global-constants
  const replaceElement = useCallback(
    (index: number, value: U) => {
      if (values[Number(index)] === value) {
        return;
      }
      setValues((prev) => {
        const newValues = [...prev];
        newValues[Number(index)] = value;
        return newValues;
      });
    },
    [values, setValues],
  );

  // eslint-disable-next-line @blumintinc/blumint/extract-global-constants
  const addElement = useCallback(() => {
    const elementNew = unwrapValue(inputElement);
    setValues((prev) => {
      return [...prev, elementNew];
    });
  }, [inputElement, setValues]);

  const removeElement = useCallback(
    (index: number) => {
      setValues((prev) => {
        return prev.filter((_, idx) => {
          return idx !== index;
        });
      });
    },
    [setValues],
  );

  return (
    <Stack sx={{ display: 'flex', flexDirection: 'column' }}>
      <Typography variant="h6" sx={{ textTransform: 'capitalize', mb: 2 }}>
        {title}:
      </Typography>
      {values.map((element, index) => {
        const initialElement = initialElements?.[Number(index)];
        return (
          <Stack
            key={`${index}-${element}}`}
            sx={{ display: 'flex', flexDirection: 'column', rowGap: 2 }}
          >
            <Stack
              sx={{
                display: 'flex',
                flexDirection: 'row',
                justifyContent: 'space-between',
                alignItems: 'center',
              }}
            >
              <Typography variant="body1">
                {(index + 1).toString().endsWith('1')
                  ? `${index + 1}st`
                  : (index + 1).toString().endsWith('2')
                  ? `${index + 1}nd`
                  : (index + 1).toString().endsWith('3')
                  ? `${index + 1}rd`
                  : `${index + 1}th`}
              </Typography>
              <GradientIconButton
                onClick={() => {
                  return removeElement(index);
                }}
                IconComponent={RemoveCircleOutlineIcon}
                gradientColor="error.horizontal"
                disableMobile
              />
            </Stack>

            <AnyInput
              input={initialElement || inputElement}
              onChange={(value) => {
                return replaceElement(index, value);
              }}
              object={element}
            />
          </Stack>
        );
      })}
      <Box sx={{ mx: 'auto', mt: -2 }}>
        <GradientIconButton
          onClick={addElement}
          gradientColor="primary.horizontal"
          IconComponent={AddCircleOutlineIcon}
          disableMobile
        />
      </Box>
    </Stack>
  );
}
